import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV, MEMBERS } from "../data/site";

const GROUPS = [
    { title: "The Dossier", items: NAV.filter((n) => !n.child && parseInt(n.no) >= 1 && parseInt(n.no) <= 6) },
    {
        title: "The Choices",
        items: NAV.filter((n) => n.no === "07" || n.child),
    },
    { title: "Our Work", items: NAV.filter((n) => !n.child && parseInt(n.no) >= 8) },
];

function Overlay({ open, onClose, pathname }) {
    useEffect(() => {
        const lenis = window.__lenis;
        if (open) lenis?.stop();
        else lenis?.start();
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="nav-menu-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-[90] bg-ink/97 backdrop-blur-xl overflow-y-auto"
                    style={{ backgroundColor: "rgba(10,10,12,0.97)" }}
                >
                    <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-24 pb-16">
                        <div className="grid gap-10 lg:grid-cols-[1fr_260px]">
                            <nav aria-label="Site sections">
                                {GROUPS.map((group, gi) => (
                                    <div key={group.title} className="mb-10">
                                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber mb-4">
                                            {group.title}
                                        </p>
                                        <ul className="space-y-1">
                                            {group.items.map((item, i) => {
                                                const active = pathname === item.to;
                                                return (
                                                    <motion.li
                                                        key={item.to}
                                                        initial={{ opacity: 0, x: -24 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: 0.05 + gi * 0.08 + i * 0.045, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                                    >
                                                        <Link
                                                            to={item.to}
                                                            onClick={onClose}
                                                            data-testid={`nav-link-${item.to === "/" ? "home" : item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                                                            className={`group flex items-baseline gap-4 border-b border-white/10 py-3 ${item.child ? "pl-4" : ""}`}
                                                        >
                                                            <span className="font-mono text-xs text-ash group-hover:text-amber transition-colors w-10">
                                                                {item.no}
                                                            </span>
                                                            <span
                                                                className={`font-display text-2xl sm:text-3xl transition-colors ${
                                                                    active ? "text-amber italic" : "text-paper group-hover:text-amber"
                                                                } ${item.child ? "text-xl sm:text-2xl" : ""}`}
                                                            >
                                                                {item.label}
                                                            </span>
                                                            {active && (
                                                                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-amber">
                                                                    ● You are here
                                                                </span>
                                                            )}
                                                        </Link>
                                                    </motion.li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </nav>
                            <aside className="hidden lg:block">
                                <div className="sticky top-0 border border-white/10 p-6">
                                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber mb-4">
                                        The Group
                                    </p>
                                    <ul className="space-y-3">
                                        {MEMBERS.map((m) => (
                                            <li key={m.name}>
                                                <p className="text-paper font-medium">{m.name}</p>
                                                <p className="font-mono text-[11px] uppercase tracking-widest text-ash">{m.role}</p>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-6 font-mono text-[10px] leading-relaxed text-ash uppercase tracking-wider">
                                        CLASSIFICATION: PUBLIC RESEARCH<br />
                                        COURSE: COMP501 S2 2026
                                    </p>
                                </div>
                            </aside>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <header
                className={`fixed top-0 inset-x-0 z-[100] border-b transition-colors duration-300 ${
                    scrolled ? "bg-ink/85 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
                }`}
            >
                <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3 group">
                        <Logo size={26} />
                        <span className="leading-none">
                            <span className="block font-display font-semibold tracking-wide text-paper text-lg group-hover:text-amber transition-colors">
                                FACE INDEX
                            </span>
                            <span className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.28em] text-ash mt-1">
                                COMP501 · NZ Police FRT Dossier
                            </span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-6">
                        <span className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ash">
                            <span className="w-1.5 h-1.5 rounded-full bg-alert animate-rec" />
                            REC · FACE INDEX
                        </span>
                        <button
                            onClick={() => setOpen(!open)}
                            data-testid="nav-menu-toggle-button"
                            aria-expanded={open}
                            className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-paper hover:border-amber hover:text-amber transition-colors"
                        >
                            {open ? <X size={14} /> : <Menu size={14} />}
                            {open ? "Close" : "Menu"}
                        </button>
                    </div>
                </div>
            </header>
            <Overlay open={open} onClose={() => setOpen(false)} pathname={pathname} />
        </>
    );
}
